// src/Pages/Rental/Rental.jsx
import { useState, useEffect, useCallback } from 'react';
import { toast } from "react-toastify";
import AOS from "aos";
import "aos/dist/aos.css";
import RentalDateSelector from '../../components/RentalDateSelector/RentalDateSelector';
import MultiLayerParallax from '../../components/MultiLayerParallax/MultiLayerParallax.tsx';
import RentalHelmetComponent from './RentalHelmetComponent';
import SoundRental from '../../components/SoundRental/SoundRental.jsx';
import LightRental from '../../components/LightRental/LightRental.jsx';
import OtherRentals from '../../components/OtherRentals/OtherRentals.jsx';
import { useBooking } from "../../context/BookingContext";
import { useLocation } from 'react-router-dom';


// Funktio, joka tarkistaa, ovatko kaksi aikaväliä päällekkäin
function isOverlap(start1, end1, start2, end2) {
  const s1 = new Date(start1);
  const e1 = new Date(end1);
  const s2 = new Date(start2);
  const e2 = new Date(end2);
  return (s1 < e2 && s2 < e1);
}

const Rental = () => {
  const { selectedDates, setSelectedDates } = useBooking();

  // AOS
  useEffect(() => {
    AOS.init({ duration: 800, once: true });
  }, []);

  const location = useLocation();

  // Puretaan päivämäärät BookingContextista
  const { startDate, endDate } = selectedDates;

  // Päivämäärien oletukset (käytetään, jos BookingContext ei ole vielä asetettu)
  const now = new Date();
  now.setHours(12, 0, 0, 0);
  const todayDateTimeLocal = now.toISOString().slice(0, 16);

  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  const tomorrowDateTimeLocal = tomorrow.toISOString().slice(0, 16);

  // --- Ostoskori-apufunktiot ---
const getCart = () => JSON.parse(localStorage.getItem("rentalCart")) || [];

// Funktio, joka laskee montako tuotetta on jo korissa
const getProductQuantityInCart = (productId, cartItems) =>
  cartItems.reduce((sum, item) => item.id === productId ? sum + item.quantity : sum, 0);



  // Tarkistetaan, onko tallennettu päivämäärä localStoragessa
  const savedDates = JSON.parse(localStorage.getItem("modalBookingData"));

  if (savedDates?.startDate) {
    const savedStart = new Date(savedDates.startDate);
    savedStart.setHours(12, 0, 0, 0);
    if (savedStart < now) {
      const updatedDates = { startDate: todayDateTimeLocal, endDate: tomorrowDateTimeLocal };
      setSelectedDates(updatedDates);
      localStorage.setItem("modalBookingData", JSON.stringify(updatedDates));
    }
  } else {
    setSelectedDates({ startDate: todayDateTimeLocal, endDate: tomorrowDateTimeLocal });
    localStorage.setItem("modalBookingData", JSON.stringify({ startDate: todayDateTimeLocal, endDate: tomorrowDateTimeLocal }));
  }

  if (!selectedDates.startDate) {
    setSelectedDates({ startDate: todayDateTimeLocal, endDate: tomorrowDateTimeLocal });
    localStorage.setItem("modalBookingData", JSON.stringify({ startDate: todayDateTimeLocal, endDate: tomorrowDateTimeLocal }));
  }

  const [cartItems, setCartItems] = useState(getCart());
  const [selectedQuantities, setSelectedQuantities] = useState({});

  // Muut tilat
  const [allDevices, setAllDevices] = useState([]);
  const [allReservations, setAllReservations] = useState([]);
  const [availableDevices, setAvailableDevices] = useState([]);

  // 1. Laitteiden haku
  const fetchRentals = useCallback(async () => {
    try {
      if (!startDate || !endDate) {
        console.warn("Päivämäärät puuttuvat, ei haeta vuokrattavia laitteita.");
        return;
      }
      const start = startDate.slice(0, 10);
      const end = endDate.slice(0, 10);

      const res = await fetch(
        `https://spiderlegsproductions.com/api/get_vuokrattavat.php?start=${start}&end=${end}`
      );
      if (!res.ok) {
        throw new Error(`Virhe laitteiden haussa: ${res.status}`);
      }

      const data = await res.json();
      if (data.success && data.items) {
        setAllDevices(data.items);
      } else {
        console.warn("API ei palauttanut odotettua dataa.");
      }
    } catch (err) {
      console.error("Virhe laitteiden haussa:", err);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchRentals();
  }, [fetchRentals]);

  // 2. Varausten haku
  const fetchReservations = useCallback(async () => {
    try {
      const res = await fetch("https://spiderlegsproductions.com/api/get_reservations.php");
      if (!res.ok) throw new Error(`Virhe varauksia haettaessa: ${res.status}`);
      const data = await res.json();
      if (data.success) {
        setAllReservations(data.reservations || []);
      }
    } catch (error) {
      console.error("Virhe varauksia haettaessa:", error);
    }
  }, []);

  useEffect(() => {
    fetchReservations();
  }, [fetchReservations]);

  // 3. checkAvailability-funktio
  const checkAvailability = useCallback(() => {
    if (!selectedDates.startDate || !selectedDates.endDate || !allDevices.length) return;

    const unavailableCount = {};
    allReservations.forEach(r => {
      if (r.start && r.end && isOverlap(selectedDates.startDate, selectedDates.endDate, r.start, r.end)) {
        r.products.forEach(p => {
          const tid = String(p.tuoteID).trim();
          const qty = parseInt(p.quantity, 10) || 1;
          if (!unavailableCount[tid]) unavailableCount[tid] = 0;
          unavailableCount[tid] += qty;
        });
      }
    });

    const updated = allDevices.map(device => {
      const totalStock = parseInt(device.available, 10) || 0;
      const used = unavailableCount[device.id] || 0;
      const stillAvailable = Math.max(totalStock - used, 0);

      const isBooked =
        (totalStock > 0 && used >= totalStock) ||
        (parseInt(device.varattuna, 10) >= 1 && stillAvailable < 1);

      return {
        ...device,
        available: stillAvailable,
        isBooked,
      };
    });

    setAvailableDevices(updated);
  }, [allDevices, allReservations, selectedDates]);

  useEffect(() => {
    checkAvailability();
  }, [checkAvailability]);

  useEffect(() => {
    const savedDates = JSON.parse(localStorage.getItem("modalBookingData"));
    if (savedDates && savedDates.startDate && savedDates.endDate) {
      setSelectedDates(savedDates);
    }
  }, [setSelectedDates]);

  useEffect(() => {
    if (location.hash) {
      const scrollToAnchor = () => {
        const id = location.hash.replace("#", "");
        const element = document.getElementById(id);
        if (element) {
          setTimeout(() => {
            element.scrollIntoView({ behavior: "smooth", block: "start" });
          }, 200);
        }
      };
      scrollToAnchor();
    }
  }, [location]);

  useEffect(() => {
    const handleCartUpdate = () => {
      setCartItems(getCart());
    };
    window.addEventListener("cartUpdated", handleCartUpdate);
    return () => window.removeEventListener("cartUpdated", handleCartUpdate);
  }, []);

  const handleRentalDatesChange = (dates) => {
    if (!dates || typeof dates.start !== "string") {
      console.warn("Päivämäärät puuttuvat:", dates);
      return;
    }
    const newDates = {
      startDate: dates.start,
      endDate: dates.end || dates.start,
    };
    setSelectedDates(newDates);
    localStorage.setItem("modalBookingData", JSON.stringify(newDates));
  };

  const addToCart = (product, quantityToAdd = 1) => {
    const productStock = parseInt(product.available, 10) || 1;
    const currentQuantityInCart = getProductQuantityInCart(product.id, cartItems);
    const availableTotal = productStock - currentQuantityInCart;
  
    if (quantityToAdd > availableTotal) {
      toast.error(`Tuotetta ${product.name} voi lisätä enintään ${availableTotal} kpl.`);
      return;
    }
  
    const updatedCart = [...cartItems];
    const existingProduct = updatedCart.find(i => i.id === product.id);
  
    if (existingProduct) {
      existingProduct.quantity += quantityToAdd;
      existingProduct.totalPrice = parseFloat(existingProduct.price) * existingProduct.quantity;
    } else {
      const price = parseFloat(product.price) || 0;
      updatedCart.push({
        id: product.id,
        name: product.name,
        slug: product.slug,
        image: product.image,
        price: price,
        quantity: quantityToAdd,
        totalPrice: price * quantityToAdd
      });
    }
  
    const totalInCart = updatedCart.find(i => i.id === product.id)?.quantity || quantityToAdd;
  
    localStorage.setItem("rentalCart", JSON.stringify(updatedCart));
  
    // Päivitetään state ja triggeröidään cartUpdated tapahtuma
    setTimeout(() => {
      setCartItems(updatedCart);
      window.dispatchEvent(new Event("cartUpdated"));
    }, 0);
  
    // ✅ Toast-logiikka
    const toastId = `toast-${product.id}`;
    const singleItem = productStock === 1;
    const allAdded = productStock > 1 && totalInCart >= productStock;
  
    const message = singleItem
      ? `${product.name} lisätty vuokrauskoriin!`
      : allAdded
      ? `Kaikki ${product.name} ovat nyt vuokrauskorissa!`
      : `${quantityToAdd} x ${product.name} lisätty vuokrauskoriin – nyt yhteensä ${totalInCart} kpl.`;
  
    if (toast.isActive(toastId)) {
      toast.update(toastId, {
        render: message,
        type: "success",
        autoClose: 3000,
        isLoading: false,
      });
    } else {
      toast.success(message, { toastId });
    }
  };
  

  const removeFromCart = (product, quantityToRemove = 1) => {
    try {
      const cart = getCart();
      const existingProduct = cart.find(i => i.id === product.id);
  
      if (!existingProduct) {
        toast.error("Tuotetta ei ole korissa.", { position: "top-right" });
        return;
      }
  
      let updatedCart = [...cart];
  
      if (existingProduct.quantity > quantityToRemove) {
        existingProduct.quantity -= quantityToRemove;
        existingProduct.totalPrice = parseFloat(existingProduct.price) * existingProduct.quantity;
        toast.info(`${quantityToRemove} x ${product.name} poistettu korista.`, {
          position: "bottom-left",
          theme: "colored"
        });
      } else {
        updatedCart = updatedCart.filter(item => item.id !== product.id);
      
        // Tarkistus: jos varastossa oli vain yksi kappale, ei näytetä "kaikki"
        const productStock = parseInt(product.available, 10) || 1;
        const message =
          productStock === 1
            ? `${product.name} on poistettu vuokrauskorista.`
            : `Kaikki ${product.name} on poistettu vuokrauskorista.`;
      
        toast.info(message, {
          position: "bottom-left",
          theme: "colored"
        });
      }
      
  
      localStorage.setItem("rentalCart", JSON.stringify(updatedCart));
      setCartItems(updatedCart);
      window.dispatchEvent(new Event("cartUpdated"));
    } catch (error) {
      toast.error("Virhe poistettaessa tuotetta korista.", { position: "top-right" });
      console.error(error);
    }
  };
  
  const incrementQuantity = (productId, available) => {
    setSelectedQuantities(prev => {
      const current = prev[productId] || 1;
      if (current < available) {
        return { ...prev, [productId]: current + 1 };
      }
      return prev;
    });
  };

  const decrementQuantity = (productId) => {
    setSelectedQuantities(prev => {
      const current = prev[productId] || 1;
      if (current > 1) {
        return { ...prev, [productId]: current - 1 };
      }
      return prev;
    });
  };

  // Kategoriakohtaiset listat
  const soundDevices = availableDevices.filter(dev => dev.kategoriaID === 1);
  const lightDevices = availableDevices.filter(dev => dev.kategoriaID === 2);
  const otherDevices = availableDevices.filter(dev => dev.kategoriaID === 3);

  return (
    <>
      <MultiLayerParallax />
      <div className="p-5 max-full mx-auto">
        <div className="dark:bg-violet bg-primary dark:bg-opacity-75 bg-opacity-75 pb-10 py-5 p-5 max-w-7xl mx-auto">
        <h1 className="font-customFont2 dark:text-shadow-xxl text-left text-darkest dark:text-primary text-5xl sm:text-6xl md:text-4xl lg:text-5xl xl:text-6xl 2xl:text-6xl font-bold leading-tight mb-4">
          Tapahtumasi alkaa tästä – valitse ajankohta ja varaa!
        </h1>

        <p className="font-customFont1 dark:text-shadow-lg text-left text-darkest dark:text-primary text-lg font-light pb-4">
          Valitse <strong>vuokrauksen aloitus- ja lopetuspäivä</strong>. Kaikki paketit ja laitteet vuokrataan <strong>vuorokausiperusteisesti</strong>, mutta <strong>peräkärry</strong> tuntiperusteisesti.
          <br /><br />
          📅 Aloituspäivän jälkeen järjestelmä ehdottaa automaattisesti seuraavaa päivää lopetukseksi. Voit muuttaa sitä, jos tarvitset pidemmän vuokra-ajan.
          <br />
          ✅ Näet heti, mitkä tuotteet ovat saatavilla. Varatut tuotteet näkyvät punaisella VARATTU-kyltillä.
          <br />
          🚚 <strong>Peräkärryn vuokrauskalenteriin</strong> pääset sen kortista.

        </p>


          <RentalDateSelector
            selectedStart={selectedDates.startDate}
            selectedEnd={selectedDates.endDate}
            onDatesChange={handleRentalDatesChange}
          />
        </div>

        {/* Äänentoisto */}
        <SoundRental
          devices={soundDevices}
          addToCart={addToCart}
          removeFromCart={removeFromCart}
          incrementQuantity={incrementQuantity}
          decrementQuantity={decrementQuantity}
          cartItems={cartItems}
          selectedQuantities={selectedQuantities}
        />

        {/* Valot */}
        <LightRental
          devices={lightDevices}
          addToCart={addToCart}
          removeFromCart={removeFromCart}
          incrementQuantity={incrementQuantity}
          decrementQuantity={decrementQuantity}
          cartItems={cartItems}
          selectedQuantities={selectedQuantities}
        />

        {/* Muut laitteet */}
        <div id="otherrentals" className="mb-32">
          <OtherRentals
            devices={otherDevices}
            addToCart={addToCart}
            removeFromCart={removeFromCart}
            incrementQuantity={incrementQuantity}
            decrementQuantity={decrementQuantity}
            cartItems={cartItems}
            selectedQuantities={selectedQuantities}
          />
        </div>
        <RentalHelmetComponent />

        {/* HUOM! Poistettu vetoauton rekisterinumero -kenttä Rental.jsx:stä */}
      </div>
    </>
  );
};

export default Rental;
